/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { TFunction } from 'i18next';

export interface PermissionMeta {
  key: string;
  label: string;
  category: string;
}

export interface PermissionEntry {
  key: string;
  label: string;
  admin: boolean;
  operational: boolean;
  broker: boolean;
}

export interface PermissionCategory {
  category: string;
  permissions: PermissionEntry[];
}

/**
 * Builds the ordered list of permission metadata with localized labels and categories.
 *
 * @param t - Translation function.
 * @returns The permission metadata entries.
 */
export function getPermissionLabels(t: TFunction): PermissionMeta[] {
  const cat = (name: string) => t(`v2.permissions.categories.${name}`);
  const label = (key: string) => t(`v2.permissions.labels.${key}`);
  const meta = (key: string, category: string): PermissionMeta => ({
    key,
    label: label(key),
    category: cat(category),
  });
  return [
    meta('add_property', 'imoveis'),
    meta('edit_property', 'imoveis'),
    meta('delete_property', 'imoveis'),
    meta('view_all_properties', 'imoveis'),
    meta('view_own_properties', 'imoveis'),
    meta('add_client', 'clientes'),
    meta('edit_client', 'clientes'),
    meta('delete_client', 'clientes'),
    meta('view_all_clients', 'clientes'),
    meta('view_own_clients', 'clientes'),
    meta('add_visit', 'visitas'),
    meta('edit_visit', 'visitas'),
    meta('delete_visit', 'visitas'),
    meta('view_agency_visits', 'visitas'),
    meta('view_own_visits', 'visitas'),
    meta('manage_agency_calendar', 'agenda'),
    meta('view_agency_calendar', 'agenda'),
    meta('manage_own_calendar', 'agenda'),
    meta('view_agency_settings', 'configuracoes'),
    meta('edit_agency', 'configuracoes'),
    meta('invite_agency_member', 'configuracoes'),
    meta('remove_member', 'configuracoes'),
    meta('edit_member_role', 'configuracoes'),
    meta('generate_report', 'configuracoes'),
  ];
}

/**
 * Returns the ordered list of localized permission category names.
 *
 * @param t - Translation function.
 * @returns The categories in display order.
 */
export function getCategoriesOrder(t: TFunction): string[] {
  return [
    t('v2.permissions.categories.imoveis'),
    t('v2.permissions.categories.clientes'),
    t('v2.permissions.categories.visitas'),
    t('v2.permissions.categories.agenda'),
    t('v2.permissions.categories.configuracoes'),
  ];
}
